import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/lib/auth';
import { usePortalClinics } from '@/hooks/usePortalClinics';

const STORAGE_KEY = 'clinio_portal_active_clinic_id';

type PortalActiveClinicContextValue = {
  activeClinicId: string | null;
  activePatientId: string | null;
  setActiveClinicId: (clinicId: string) => void;
  clinics: NonNullable<ReturnType<typeof usePortalClinics>['data']>;
  isLoading: boolean;
};

const PortalActiveClinicContext = createContext<PortalActiveClinicContextValue | null>(null);

export function PortalActiveClinicProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: clinics = [], isLoading } = usePortalClinics();
  const [activeClinicId, setActiveClinicIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const setActiveClinicId = useCallback((clinicId: string) => {
    setActiveClinicIdState(clinicId);
    try {
      localStorage.setItem(STORAGE_KEY, clinicId);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!user?.id || clinics.length === 0) return;

    const valid = activeClinicId && clinics.some((c) => c.clinic_id === activeClinicId);
    if (!valid) {
      setActiveClinicId(clinics[0].clinic_id);
    }
  }, [user?.id, clinics, activeClinicId, setActiveClinicId]);

  const activePatientId = useMemo(() => {
    if (clinics.length === 0) return null;

    if (activeClinicId) {
      const match = clinics.find((c) => c.clinic_id === activeClinicId);
      if (match) return match.patient_id;
    }

    return clinics[0]?.patient_id ?? null;
  }, [activeClinicId, clinics]);

  const value = useMemo(
    () => ({
      activeClinicId,
      activePatientId,
      setActiveClinicId,
      clinics,
      isLoading,
    }),
    [activeClinicId, activePatientId, setActiveClinicId, clinics, isLoading],
  );

  return (
    <PortalActiveClinicContext.Provider value={value}>
      {children}
    </PortalActiveClinicContext.Provider>
  );
}

export function usePortalActiveClinic() {
  const ctx = useContext(PortalActiveClinicContext);
  if (!ctx) {
    throw new Error('usePortalActiveClinic must be used within PortalActiveClinicProvider');
  }
  return ctx;
}
